import React, { useState, useCallback, useMemo } from 'react'
import { motion } from 'framer-motion'
import { 
  Shield, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Clock,
  User,
  Database,
  Eye,
  ChevronRight,
  TrendingUp
} from 'lucide-react'
import ReactJsonView from 'react-json-view'

import { DSAPMockServer, Scenario, DSAPRequest, DSAPResponse } from '../mock/dsap-mock'

interface Props {
  onComplete: (data: any) => void
  selectedScenario: Scenario | null
  agentCard: any
  mockServer: DSAPMockServer
  addToHistory: (request: DSAPRequest, response: DSAPResponse) => void
}

export default function Step2VerifyAccess({
  onComplete,
  selectedScenario,
  agentCard,
  mockServer,
  addToHistory
}: Props) {
  const [loading, setLoading] = useState(false)
  const [accessDecision, setAccessDecision] = useState<any>(null)
  const [showRequestJson, setShowRequestJson] = useState(false)
  const [showResponseJson, setShowResponseJson] = useState(false)

  const requestPayload = useMemo(() => {
    if (!selectedScenario) return null
    
    return {
      jsonrpc: '2.0',
      method: 'dsap.verifyAccess',
      params: {
        agentId: selectedScenario.agentId,
        userId: selectedScenario.userId,
        dataResource: selectedScenario.dataResource,
        operation: selectedScenario.operation,
        context: selectedScenario.context
      },
      id: Date.now()
    }
  }, [selectedScenario])

  const handleRequestAccess = useCallback(async () => {
    if (!requestPayload) return
    
    setLoading(true)
    
    try {
      const response = await mockServer.processRequest(requestPayload)
      addToHistory(requestPayload, response)
      
      if (response.result) {
        setAccessDecision(response.result)
        // Auto-show response details
        setTimeout(() => setShowResponseJson(true), 1000)
      }
    } catch (error) {
      console.error('Access verification failed:', error)
    } finally {
      setLoading(false)
    }
  }, [requestPayload, mockServer, addToHistory])

  const handleContinue = useCallback(() => {
    onComplete({ accessDecision })
  }, [onComplete, accessDecision])

  const getRiskLevel = (score: number) => {
    if (score <= 30) return { level: 'Low', color: 'text-green-600', bg: 'bg-green-100' }
    if (score <= 70) return { level: 'Medium', color: 'text-yellow-600', bg: 'bg-yellow-100' }
    return { level: 'High', color: 'text-red-600', bg: 'bg-red-100' }
  }

  const risk = accessDecision ? getRiskLevel(accessDecision.riskScore) : null

  return (
    <div className="space-y-6">
      {/* Step Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-dsap-100 rounded-lg flex items-center justify-center">
              <Shield className="h-6 w-6 text-dsap-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Step 2: Request Data Access Authorization
            </h3>
            <p className="text-gray-600 mb-4">
              Submit an access verification request to the DSAP server. The server will evaluate 
              the request against security policies, user roles, and compliance requirements to 
              make an authorization decision.
            </p>
            
            {selectedScenario && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="font-medium text-blue-900 mb-2">Current Scenario Context</h5>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-blue-600 font-medium">Business Purpose:</span>
                    <br />
                    {selectedScenario.context.businessPurpose.replace(/_/g, ' ')}
                  </div>
                  <div>
                    <span className="text-blue-600 font-medium">Data Types:</span>
                    <br />
                    {selectedScenario.context.sensitivity.join(', ')}
                  </div>
                  <div>
                    <span className="text-blue-600 font-medium">Expected Risk:</span>
                    <br />
                    {selectedScenario.expectedRiskScore}/100
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Access Request Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">
          Access Request Details
        </h4>
        
        {selectedScenario && requestPayload && (
          <div className="space-y-4 mb-6">
            {/* Request Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <User className="h-5 w-5 text-gray-600" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Agent Identity</div>
                    <div className="text-sm text-gray-600">{selectedScenario.agentId}</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Database className="h-5 w-5 text-gray-600" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Data Resource</div>
                    <div className="text-sm text-gray-600">{selectedScenario.dataResource}</div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <User className="h-5 w-5 text-gray-600" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">User Context</div>
                    <div className="text-sm text-gray-600">{selectedScenario.userId}</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Eye className="h-5 w-5 text-gray-600" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Operation</div>
                    <div className="text-sm text-gray-600 capitalize">{selectedScenario.operation}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Requested Fields */}
            <div>
              <div className="text-sm font-medium text-gray-900 mb-2">Requested Data Fields</div>
              <div className="flex flex-wrap gap-2">
                {selectedScenario.context.requestedFields.map((field) => (
                  <span
                    key={field}
                    className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                  >
                    {field}
                  </span>
                ))}
              </div>
            </div>

            {/* Request JSON Toggle */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowRequestJson(!showRequestJson)}
                className="text-dsap-600 hover:text-dsap-700 font-medium text-sm"
              >
                {showRequestJson ? 'Hide' : 'Show'} JSON-RPC Request
              </button>
              
              {showRequestJson && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4"
                >
                  <ReactJsonView
                    src={requestPayload}
                    theme="monokai"
                    collapsed={false}
                    displayDataTypes={false}
                    displayObjectSize={false}
                    enableClipboard={true}
                    name="request"
                  />
                </motion.div>
              )}
            </div>
          </div>
        )}

        {/* Submit Request */}
        {!accessDecision ? (
          <div className="text-center py-6">
            <button
              onClick={handleRequestAccess}
              disabled={loading || !selectedScenario}
              className={`inline-flex items-center px-8 py-4 text-lg font-medium rounded-lg transition-all ${
                loading
                  ? 'bg-dsap-400 text-white cursor-wait'
                  : 'bg-dsap-600 text-white hover:bg-dsap-700 transform hover:scale-105'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3" />
                  Requesting Access...
                </>
              ) : (
                <>
                  <Shield className="h-5 w-5 mr-3" />
                  Request Access Authorization
                </>
              )}
            </button>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            {/* Access Decision Header */}
            <div className={`p-6 rounded-lg border-2 ${
              accessDecision.allowed
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  accessDecision.allowed ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {accessDecision.allowed ? (
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h4 className={`text-xl font-bold ${
                    accessDecision.allowed ? 'text-green-900' : 'text-red-900'
                  }`}>
                    Access {accessDecision.allowed ? 'GRANTED' : 'DENIED'}
                  </h4>
                  <p className={`text-sm ${
                    accessDecision.allowed ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {accessDecision.policyReason}
                  </p>
                </div>
                {risk && (
                  <div className="text-right">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      risk.bg
                    } ${risk.color}`}>
                      <TrendingUp className="h-4 w-4 mr-1" />
                      {risk.level} Risk
                    </div>
                    <div className="text-lg font-bold text-gray-700 mt-1">
                      {accessDecision.riskScore}/100
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Access Details */}
            {accessDecision.allowed && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Allowed Fields */}
                {accessDecision.restrictions?.allowedFields?.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h5 className="font-medium text-green-900 mb-3 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Allowed Fields
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {accessDecision.restrictions.allowedFields.map((field: string) => (
                        <span
                          key={field}
                          className="status-pill status-pill-allowed"
                        >
                          {field}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Denied Fields */}
                {accessDecision.restrictions?.deniedFields?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h5 className="font-medium text-red-900 mb-3 flex items-center">
                      <XCircle className="h-4 w-4 mr-2" />
                      Denied Fields
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {accessDecision.restrictions.deniedFields.map((field: string) => (
                        <span
                          key={field}
                          className="status-pill status-pill-denied"
                        >
                          {field}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Conditions and Restrictions */}
            {accessDecision.allowed && (
              <div className="space-y-4">
                {accessDecision.conditions?.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h5 className="font-medium text-yellow-900 mb-3 flex items-center">
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Access Conditions
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {accessDecision.conditions.map((condition: string) => (
                        <span
                          key={condition}
                          className="status-pill status-pill-warning"
                        >
                          {condition.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {accessDecision.restrictions?.maxRecords && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h5 className="font-medium text-blue-900 mb-2 flex items-center">
                      <Database className="h-4 w-4 mr-2" />
                      Data Limits
                    </h5>
                    <div className="text-sm text-blue-800">
                      Maximum {accessDecision.restrictions.maxRecords} records allowed
                    </div>
                  </div>
                )}

                {accessDecision.ttl && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 mb-2 flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      Time Limit
                    </h5>
                    <div className="text-sm text-gray-700">
                      Access expires in {Math.floor(accessDecision.ttl / 60)} minutes
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Response JSON */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowResponseJson(!showResponseJson)}
                className="text-dsap-600 hover:text-dsap-700 font-medium text-sm"
              >
                {showResponseJson ? 'Hide' : 'Show'} JSON-RPC Response
              </button>
              
              {showResponseJson && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4"
                >
                  <ReactJsonView
                    src={accessDecision}
                    theme="monokai"
                    collapsed={1}
                    displayDataTypes={false}
                    displayObjectSize={false}
                    enableClipboard={true}
                    name="response"
                  />
                </motion.div>
              )}
            </div>

            {/* Continue Button */}
            <div className="flex justify-end pt-4">
              <button
                onClick={handleContinue}
                className="inline-flex items-center px-6 py-3 bg-dsap-600 text-white font-medium rounded-lg hover:bg-dsap-700 transition-colors"
              >
                Continue to Activity Logging
                <ChevronRight className="h-4 w-4 ml-2" />
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}